import { useAuth } from "../../components/context/LoginContext/AuthContext";
import { useAuthStudent } from "../../components/context/StudentLoginContext/StudentAuthContext";
import { useAutoSwitchStudent } from "../../hooks/web/useAutoSwitchStudent";
import { authNavigator } from "../authNavigator";

const useHandleSuccess = () => {
  const { fetchAndSwitchToFirstStudent } = useAutoSwitchStudent();
  const { studentLogin } = useAuthStudent();
  const { login, ClearUser } = useAuth();

  const handleSuccess = async ({
    data,
    redirect,
    tab,
    navigate,
    navigation = true,
  }) => {
    const user = data?.user;
    const student = data?.student;

    const isParent = user?.role_id === 2 || user?.role === 2;
    const isSubscribed =
      user?.subscription_data?.is_subscribed ?? user?.is_subscribed ?? false;
    const parentType = user?.parent_type;

    let finalData = data;

    // ─────────────────────────────────────────────
    // CASE 1: School Parent or Unsubscribed Mixed Parent → Normal Parent Login
    // ─────────────────────────────────────────────
    if (
      isParent &&
      (parentType === "school_parent" ||
        (parentType === "mixed" && !isSubscribed))
    ) {
      login(data);
    }

    // ─────────────────────────────────────────────
    // CASE 2: Subscribed/Paid Parent → Normal Login
    // ─────────────────────────────────────────────
    else if (isParent && isSubscribed) {
      login(data);
    }

    // ─────────────────────────────────────────────
    // CASE 3: Unsubscribed Normal Parent → Switch to Student
    // ─────────────────────────────────────────────
    else if (isParent && !isSubscribed) {
      //  Do NOT call login(data) here — avoids writing stale parent tokens
      const currentToken = data?.token || data?.accessToken;

      const switchRes = await fetchAndSwitchToFirstStudent(false, currentToken);

      if (switchRes) {
        const newToken = switchRes?.token;
        const newRefreshToken =
          switchRes?.refresh_token || switchRes?.refreshToken;
        const newStudent = switchRes?.student;

        //  Validate before writing
        if (!newToken && !newRefreshToken) {
          return;
        }

        const newUserStorageObj = {
          token: newToken,
          refreshToken: newRefreshToken, //  camelCase for your app
          student: {
            ...newStudent,
            token: newToken,
          },
        };

        finalData = {
          student: {
            ...newStudent,
            token: newToken,
          },
          token: newToken,
          accessToken: newToken,
          refreshToken: newRefreshToken,
          refresh_token: newRefreshToken,
        };

        ClearUser();

        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        localStorage.removeItem("student");

        //  Save correctly mapped keys
        localStorage.setItem("accessToken", newToken);
        localStorage.setItem("refreshToken", newRefreshToken);
        localStorage.setItem("student", JSON.stringify(newUserStorageObj));

        studentLogin(finalData);
      } else {
        // Fallback: switch API failed or returned empty
        login(data);
      }
    }

    // ─────────────────────────────────────────────
    // CASE 3: Direct Student Login
    // ─────────────────────────────────────────────
    else if (student) {
      studentLogin(data);
    }

    // ─────────────────────────────────────────────
    // CASE 4: Any Other User (admin, teacher, etc.)
    // ─────────────────────────────────────────────
    else if (user) {
      login(data);
    }

    if (!navigation) {
      return;
    }

    // ─────────────────────────────────────────────
    // STEP FINAL: Navigate
    // ─────────────────────────────────────────────
    authNavigator({
      user: finalData?.student || finalData?.user,
      redirect,
      tab,
      navigate,
    });
  };

  return { handleSuccess };
};

export default useHandleSuccess;
